import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Card,
  CrudMode,
  Dialog,
  handleClose,
  Page,
  PageRoute,
  ScreenType,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import {
  StaffEmployment,
  StaffEmploymentEditResource,
  StaffEmploymentViewResource,
  Permission,
} from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { StaffEmploymentPageBody } from "./StaffEmploymentPageBody";
import { StaffEmploymentPageHeader } from "./StaffEmploymentPageHeader";

const routeName = "staffEmployment";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
  screenType: ScreenType = "standard";
}

class OptionalParams {
  staffId?: number = 0;
}

export const staffEmploymentPageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Employment",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export const StaffEmploymentPage = () => {
  const ROOT_PERMISSION: Permission = Permission.StaffEmployment;
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const navigate = useNavigate();
  const [employment, setEmployment] = useState<StaffEmployment>({} as StaffEmployment);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [employment] });
  const [viewResource, setViewResource] = useState<StaffEmploymentViewResource>({});
  const [editResource, setEditResource] = useState<StaffEmploymentEditResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.id);

    ApiService.staffEmployments.getViewResource().then((response) => {
      setViewResource(response);
    });
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit")
      ApiService.staffEmployments.getEditResource().then((response) => {
        setEditResource(response);
      });
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${staffEmploymentPageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType })}`);

  const fetch = (id: number) => {
    ApiService.staffEmployments.get(id).then((response) => {
      if (id === 0 && optionalParams.staffId) response.staffId = optionalParams.staffId;

      setEmployment(response);
      resetUnsavedChanges([response]);
    });
  };

  const pageHeader = (
    <StaffEmploymentPageHeader
      rootPermission={ROOT_PERMISSION}
      handle={staffEmploymentPageRoute.handle}
      crudMode={params.crudMode}
      reroute={reroute}
      fetch={fetch}
      employment={employment}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
    />
  );

  const pageBody = (
    <StaffEmploymentPageBody
      crudMode={params.crudMode}
      employment={employment}
      setEmployment={setEmployment}
      viewResource={viewResource}
      editResource={editResource}
      validationErrors={validationErrors}
    />
  );

  return params.screenType === "standard" ? (
    <Page isForm header={pageHeader}>
      <Card>{pageBody}</Card>
    </Page>
  ) : (
    <Dialog
      isForm
      open={true}
      onClose={() => handleClose(navigate, unsavedChanges)}
      maxWidth="md"
      header={pageHeader}
      content={pageBody}
    />
  );
};
